import { useContext } from 'react';
import { FormikContext } from 'formik';
import { NumericFormat } from 'react-number-format';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps, TextField } from '@mui/material';
import Typography from '../Typography';
import { FormTrackingContext } from '../Form';
import InfoButton from '../InfoButton';
import { logFieldChange } from '../../utils/tracking';
import { COLORS } from '../../themev2';
import { CommonProps } from './types';

type NumericFormatFieldProps = CommonProps & {
  value: string | number;
  sx?: SxProps;
  decimalScale?: number;
};

const NumericFormatField = ({
  name,
  error,
  fullWidth,
  label,
  value,
  onChange,
  hideLabel = false,
  eventLogger = logFieldChange,
  sx,
  placeholder,
  moreInfoTooltip,
  disabled = false,
  decimalScale = 2,
}: NumericFormatFieldProps) => {
  const formik = useContext(FormikContext);
  const { trackingEvent } = useContext(FormTrackingContext);
  const _error = error || (formik?.touched[name] && formik?.errors[name]);

  return (
    <FormControl fullWidth={fullWidth} error={!!_error}>
      {hideLabel ? null : (
        <FormLabel
          sx={{
            padding: '8px 0',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography>{label}</Typography>
          {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
        </FormLabel>
      )}
      <NumericFormat
        disabled={disabled}
        name={name}
        thousandSeparator
        decimalSeparator="."
        fixedDecimalScale
        decimalScale={decimalScale}
        defaultValue={0}
        value={value}
        onValueChange={onChange}
        allowNegative={false}
        customInput={TextField}
        error={!!_error}
        sx={{
          '.MuiInputBase-input': {
            padding: '12px',
          },
          background: COLORS.brand.white,
          ...sx,
        }}
        onBlur={(e: any) => {
          formik?.handleBlur({ target: { name } });
          const value = e.target.value;
          trackingEvent && e.target.hasChanged && eventLogger(trackingEvent, name, value);
          e.target.hasChanged = false;
        }}
        placeholder={placeholder}
      />
      {!!_error && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default NumericFormatField;
